import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAppContext } from "../config/context"
import { useHeaders } from "./axios-headers"
import { extract } from "../client/backend-client/AxiosUtils"
import { CodeLibelle } from "../client/backend-client/generated"

export const useGetStructureSector = (structureId: string) => {
  const { sectorsResourceApi } = useAppContext()
  const getHeaders = useHeaders()

  const { isLoading, data } = useQuery({
    queryKey: ["structure-sector", structureId],
    queryFn: async () => {
      const { headers } = await getHeaders()
      return sectorsResourceApi.getStructureSector({ structureId }, { headers }).then(extract)
    },
    retry: 0,
    meta: {
      errorMessage: `Erreur lors de la récupération des filières structure: ${structureId}`
    }
  })

  return {
    isLoading,
    data
  }
}

export const useGetProjectSector = (projectId: number) => {
  const { sectorsResourceApi } = useAppContext()
  const getHeaders = useHeaders()

  const { isLoading, data } = useQuery({
    queryKey: ["project-sector", projectId],
    queryFn: async () => {
      const { headers } = await getHeaders()
      return sectorsResourceApi.getProjectSector({ projectId }, { headers }).then(extract)
    },
    retry: 0,
    meta: {
      errorMessage: `Erreur lors de la récupération des filières projet: ${projectId}`
    }
  })

  return {
    isLoading,
    data
  }
}

export const useUpdateStructureSector = (structureId: string, projectId: number) => {
  const { sectorsResourceApi } = useAppContext()
  const getHeaders = useHeaders()
  const queryClient = useQueryClient()

  return useMutation({
    scope: {
      id: `update-sector-structure-${structureId}`
    },
    mutationFn: async (sector: CodeLibelle) => {
      const { headers } = await getHeaders()
      return sectorsResourceApi
        .updateStructureSector({ structureId, projectId, codeLibelle: sector }, { headers })
        .then(extract)
    },
    onSuccess: (result) => {
      queryClient.setQueryData<CodeLibelle>(["structure-sector", structureId], result)
      queryClient.invalidateQueries({ queryKey: ["project-sector", projectId] })
      queryClient.invalidateQueries({ queryKey: ["structure", structureId] })
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
    },
    meta: {
      errorMessage: `Erreur lors de la mise à jour de la filière structure: ${structureId}`
    }
  })
}

export const useUpdateProjectSector = (projectId: number) => {
  const { sectorsResourceApi } = useAppContext()
  const getHeaders = useHeaders()
  const queryClient = useQueryClient()

  return useMutation({
    scope: {
      id: `update-sector-project-${projectId}`
    },
    mutationFn: async (sector: CodeLibelle) => {
      const { headers } = await getHeaders()
      return sectorsResourceApi
        .updateProjectSector({ projectId, codeLibelle: sector }, { headers })
        .then(extract)
    },
    onSuccess: (result) => {
      queryClient.setQueryData<CodeLibelle>(["project-sector", projectId], result)
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
    },
    meta: {
      errorMessage: `Erreur lors de la mise à jour de la filière projet:${projectId}`
    }
  })
}
